'use client'
import { Star, Quote } from 'lucide-react'
import { Card } from '@/components/ui/card'
import { Media } from '@/components/Media'
import RichText from '@/components/RichText'
import { use, useState } from 'react'

interface TestimonialCardProps {
  quote: any
  name: string
  title: any
  avatar: string
  rating: number
}

export function TestimonialCard({ quote, name, title, avatar, rating }: TestimonialCardProps) {
  const [isExpanded, setIsExpanded] = useState(false)

  const renderStars = () => {
    return Array.from({ length: 5 }, (_, index) => (
      <Star
        key={index}
        className={`${index < rating ? 'text-yellow-400 fill-yellow-400' : 'text-gray-300 fill-gray-300'} h-5 w-5 md:h-6 md:w-6`}
      />
    ))
  }

  return (
    <Card className="border border-border p-4 rounded-2xl shadow-sm w-full h-auto flex flex-col bg-background">
      <div className="flex flex-col h-full">
        {/* Quote Icon and Stars */}
        <div className="flex justify-between items-start mb-2 md:mb-4">
          <Quote className="h-10 w-10 md:h-12 md:w-12 text-primary rotate-180 flex-shrink-0" />
          <div className="flex flex-wrap space-x-1 max-w-[140px] md:max-w-none overflow-hidden">
            {renderStars()}
          </div>
        </div>

        {/* Testimonial Text - Takes available space */}
        <div className="flex-1 ">
          {quote && (
            <RichText
              data={quote}
              enableGutter={false}
              enableProse={false}
              className={`text-sm sm:text-base md:text-lg mb-2 md:mb-4 text-muted-foreground break-words ${
                isExpanded ? '' : 'line-clamp-4'
              }`}
            />
          )}
          <button
            onClick={() => setIsExpanded((prev) => !prev)}
            className="text-primary text-sm md:text-base font-medium hover:underline"
          >
            {isExpanded ? 'Close' : 'Read More'}
          </button>
        </div>

        {/* Author Info - Fixed at bottom */}
        <div className="flex items-center space-x-3 md:space-x-4 pt-3 md:pt-4 border-t border-border mt-auto">
          <div className="relative flex-shrink-0">
            <Media
              resource={avatar}
              imgClassName="w-12 h-12 md:w-14 md:h-14 rounded-full object-cover ring-2 ring-border"
            />
          </div>
          <div className="min-w-0 flex-1">
            <h3 className="text-base sm:text-lg md:text-xl lg:text-2xl font-semibold mb-1 md:mb-2 line-clamp-2 break-words text-black">
              {name}
            </h3>
            {title && (
              <RichText
                data={title}
                enableGutter={false}
                enableProse={false}
                className="text-foreground text-xs sm:text-sm md:text-base line-clamp-1"
              />
            )}
          </div>
        </div>
      </div>
    </Card>
  )
}
